/* motion: sheet — bottom sheets on phones, driven by the app's own `hidden` toggling. */
import './sheet.css';

const PHONE = '(max-width: 640px)';
const PANELS = ['instructions-panel', 'app-menu'] as const;
const TRIGGER: Record<string, string> = {
  'instructions-panel': 'btn-instructions',
  'app-menu': 'btn-menu',
};

export function init(root: HTMLElement): void {
  const phone = window.matchMedia(PHONE);
  const backdrop = document.createElement('div');
  backdrop.className = 'sheet-backdrop';
  backdrop.hidden = true;
  document.body.appendChild(backdrop);

  const panelEl = (id: string) => root.querySelector<HTMLElement>(`#${id}`);
  const openPanel = () => PANELS.map(panelEl).find((p) => p && !p.hasAttribute('hidden')) ?? null;
  let current: HTMLElement | null = null;

  const sync = () => {
    const open = openPanel();
    if (open === current) return;
    const prev = current;
    current = open;
    const sheet = Boolean(open) && phone.matches;
    document.documentElement.classList.toggle('sheet-open', sheet);
    backdrop.hidden = !sheet;
    if (open) {
      // Focus the sheet's first control after the paint that shows it.
      requestAnimationFrame(() => {
        const first = open.querySelector<HTMLElement>('button, [href], input, [tabindex]');
        (first ?? open).focus({ preventScroll: true });
      });
    } else if (prev) {
      const a = document.activeElement;
      // Only pull focus back if it was inside the sheet (or lost to the body).
      if (!a || a === document.body || prev.contains(a)) {
        root.querySelector<HTMLElement>(`#${TRIGGER[prev.id]}`)?.focus({ preventScroll: true });
      }
    }
  };

  // The app owns open/close via `hidden`; we only react to it.
  const mo = new MutationObserver(sync);
  for (const id of PANELS) {
    const p = panelEl(id);
    if (p) mo.observe(p, { attributes: true, attributeFilter: ['hidden'] });
  }
  phone.addEventListener('change', () => {
    current = null;
    sync();
  });

  // Close through the app's own trigger so aria-expanded and the label stay correct.
  const close = () => {
    if (current) root.querySelector<HTMLElement>(`#${TRIGGER[current.id]}`)?.click();
  };
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && current) close();
  });
}
